import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HandoverTaskResponse, HandoverTask, HandoverTaskState } from '@handover/shared-types';
import { handoverTaskApi } from './handoverTaskApi';

// Initial state
const initialState: HandoverTaskState = {
  handoverTaskDocData: null,
  accessDocData: null,
  isCreatedBy: null,
  isPermittedUser: null,
  isBlockedUser: null,
  isWriteUser: null,
  message: '',
  status: 'idle',
  error: null,
};

// Slice definition
const handoverTaskSlice = createSlice({
  name: 'handoverTask',
  initialState,
  reducers: {
    setHandoverTaskDocState: (state, action: PayloadAction<HandoverTask[]>) => {
      state.handoverTaskDocData = action.payload;
    },
    setAccessDocState: (state, action: PayloadAction<object>) => {
      state.accessDocData = action.payload;
    },
    setUserRolePermissions: (
      state,
      action: PayloadAction<{
        isCreatedBy: boolean;
        isPermittedUser: boolean;
        isBlockedUser: boolean;
        isWriteUser: boolean;
      }>
    ) => {
      const { isCreatedBy, isPermittedUser, isBlockedUser, isWriteUser } = action.payload;
      state.isCreatedBy = isCreatedBy;
      state.isPermittedUser = isPermittedUser;
      state.isBlockedUser = isBlockedUser;
      state.isWriteUser = isWriteUser;
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
    resetHandoverTaskState: state => {
      state.handoverTaskDocData = null;
      state.accessDocData = null;
      state.isCreatedBy = null;
      state.isPermittedUser = null;
      state.isBlockedUser = null;
      state.isWriteUser = null;
      state.message = '';
      state.status = 'idle';
      state.error = null;
    },
    setStatus: (state, action: PayloadAction<'idle' | 'loading' | 'succeeded' | 'failed'>) => {
      state.status = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: builder => {
    // Handle handoverTaskApi actions
    builder
      .addMatcher(handoverTaskApi.endpoints.getHandoverTask.matchPending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addMatcher(
        handoverTaskApi.endpoints.getHandoverTask.matchFulfilled,
        (state, action: PayloadAction<HandoverTaskResponse>) => {
          state.status = 'succeeded';
          state.handoverTaskDocData = action.payload.handoverTaskDoc.tasks;
          state.accessDocData = action.payload.handoverAccessDoc;
          state.isCreatedBy = action.payload.isCreatedBy;
          state.isPermittedUser = action.payload.isPermittedUser;
          state.isWriteUser = action.payload.isWriteUser;
          state.isBlockedUser = action.payload.isBlockedUser;
          state.message = action.payload.message;
        }
      )
      .addMatcher(handoverTaskApi.endpoints.getHandoverTask.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message || 'Failed to fetch handover task document';
      })
      .addMatcher(handoverTaskApi.endpoints.updateHandoverTask.matchPending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addMatcher(
        handoverTaskApi.endpoints.updateHandoverTask.matchFulfilled,
        (state, action: PayloadAction<HandoverTaskResponse>) => {
          state.status = 'succeeded';
          state.handoverTaskDocData = action.payload.handoverTaskDoc.tasks;
          state.message = action.payload.message;
        }
      )
      .addMatcher(handoverTaskApi.endpoints.updateHandoverTask.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message || 'Failed to update handover task';
      })
      .addMatcher(handoverTaskApi.endpoints.deleteHandoverTask.matchPending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addMatcher(
        handoverTaskApi.endpoints.deleteHandoverTask.matchFulfilled,
        (state, action: PayloadAction<HandoverTaskResponse>) => {
          state.status = 'succeeded';
          state.handoverTaskDocData = action.payload.handoverTaskDoc.tasks;
          state.message = action.payload.message;
        }
      )
      .addMatcher(handoverTaskApi.endpoints.deleteHandoverTask.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message || 'Failed to delete handover task';
      });
  },
});

// Export actions
export const {
  setHandoverTaskDocState,
  setAccessDocState,
  setUserRolePermissions,
  setMessage,
  resetHandoverTaskState,
  setStatus,
  setError,
} = handoverTaskSlice.actions;

// Export reducer
export default handoverTaskSlice.reducer;
